/**
 * 🗃️ SMART REGISTRY - Dependency Injection Container
 * --------------------------------------------------
 * This singleton registry manages all core services (Auth, Store, Monitoring, etc.)
 * It replaces direct window globals, making the architecture cleaner and testable.
 *
 * YA NO DECLARA LOS SERVICIOS DEL PANEL. Se fueron con él a `apps/backoffice`,
 * que tiene su propio registro. Mientras estuvieron aquí, este `ServiceMap`
 * referenciaba el tipo de CADA servicio de la app con un `import()` en línea, y
 * como lo importa medio repositorio, ataba todo con todo: cualquier cálculo de
 * "qué se lleva el back office al mudarse" daba el repositorio entero.
 */

import { Logger } from '@theidiomaclub/logging';

// Define exact interfaces for each service to enable strict typing
export interface ServiceMap {
    'Auth': import('./auth').SmartAuthType;
    'Store': import('./store').IReactiveStore;
    'Database': import('./supabase').DatabaseModuleType;
    'Supabase': import('@supabase/supabase-js').SupabaseClient;
    'Router': import('./MainRouter').MainRouter;
    'Navigation': unknown;
    'OfflineDatabase': import('../infrastructure/persistence/OfflineDatabase').OfflineDatabaseClass;
    'SyncManager': typeof import('./sync-manager').default;
    'ErrorHandler': import('./error-handler').IErrorHandler;

    'CourseRepository': import('../domain/repositories/ICourseRepository').ICourseRepository;
    'LessonRepository': import('../domain/repositories/ILessonRepository').ILessonRepository;
    'SessionRepository': import('../domain/repositories/ISessionRepository').ISessionRepository;
    'SmartDatePicker': import('../../shared/components/SmartPickers').SmartDatePicker;
    'SmartTimePicker': import('../../shared/components/SmartPickers').SmartTimePicker;
    'NotificationRepository': import('../infrastructure/repositories/NotificationRepository').NotificationRepository;
    'NotificationService': import('./NotificationService').NotificationService;
    'ProfileRepository': import('../domain/repositories/IProfileRepository').IProfileRepository;
    'UserProgressRepository': import('../infrastructure/repositories/UserProgressRepository').UserProgressRepository;
    'CohortRepository': import('../domain/repositories/ICohortRepository').ICohortRepository;


    // === Feature Services (flexible typing) ===
    'LessonEngine': unknown;
    'LessonService': unknown;
    'MapFeature': unknown;
    'ProfileFeature': unknown;
    'ABTesting': unknown;
    'CardRegistry': unknown;
    'AnalyticsObserver': import('./analytics-observer').IAnalyticsObserver;
    'SmartNotify': import('./notify').ISmartNotify;
    'SmartTransitions': unknown;
    'Transitions': unknown;
    'EventTracker': import('./EventTracker').EventTrackerService;

    'UsersFeature': unknown;
    'CohortsFeature': unknown;
    'CoursesFeature': unknown;
    'CurriculumFeature': unknown;
    'TeachersFeature': unknown;
    'SessionsFeature': unknown;
    'RiskFeature': unknown;
    'AgendaFeature': unknown;
    'RealtimeService': import('./realtime').RealtimeService;
    'SubscriptionRepository': import('../domain/repositories/ISubscriptionRepository').ISubscriptionRepository;
    'Celebration': import('./CelebrationService').CelebrationService;

    // Other Services
    'Bootstrapper': unknown;
    'Constants': unknown;
    'WorldSelector': { open(): void; close(): void };
    'UIBindings': unknown;
    'CurrencyService': import('./currency-service').CurrencyService;
    'Sentry': {
        setUser(user: Record<string, unknown> | null): void;
        addBreadcrumb(message: string, category: string, data: Record<string, unknown>): void;
        captureError(error: unknown, context: Record<string, unknown>): void;
    };
}

export type ServiceName = keyof ServiceMap | string;

export class SmartRegistry {
    private _services: Map<ServiceName, unknown> = new Map();
    private _waiting: Map<ServiceName, ((instance: unknown) => void)[]> = new Map();

    constructor() {
        Logger.system('SmartRegistry: Initiated');
    }

    /**
     * Register a service and notify waiters
     */
    register<K extends keyof ServiceMap>(name: K, instance: ServiceMap[K]): void;
    register(name: ServiceName, instance: unknown): void;
    register(name: ServiceName, instance: unknown): void {
        // Idempotent: silently overwrite (React StrictMode re-mounts hooks that re-register)
        this._services.set(name, instance);
        // 🍎 SILENCE: Only log essential registrations in non-debug mode
        const isDev = import.meta.env.DEV;
        if (isDev && (window as unknown as Record<string, unknown>).DEBUG_REGISTRY) {
            Logger.debug(`SmartRegistry: '${name}' registered`);
        }

        // Resolve any promises waiting for this service
        const waiters = this._waiting.get(name);
        if (waiters) {
            waiters.forEach(resolve => resolve(instance));
            this._waiting.delete(name);
        }

        // Legacy/Hook support: Dispatch event
        window.dispatchEvent(new CustomEvent(`service_ready_${name.toLowerCase()}`, { detail: instance }));
    }

    /**
     * Get a service immediately with proper typing
     */
    get<K extends keyof ServiceMap>(name: K): ServiceMap[K] | null;
    get<T>(name: ServiceName): T | null;
    get<T>(name: ServiceName): T | null {
        return (this._services.get(name) as T) || null;
    }

    /**
     * Wait for a service to be registered
     */
    whenReady<K extends keyof ServiceMap>(name: K): Promise<ServiceMap[K]>;
    whenReady<T>(name: ServiceName): Promise<T>;
    whenReady<T>(name: ServiceName): Promise<T> {
        const existing = this._services.get(name);
        if (existing) return Promise.resolve(existing as T);

        return new Promise<T>((resolve) => {
            const waiters = this._waiting.get(name) || [];
            waiters.push((instance: unknown) => resolve(instance as T));
            this._waiting.set(name, waiters);
        });
    }

    has(name: ServiceName): boolean {
        return this._services.has(name);
    }

    getRegisteredServices(): ServiceName[] {
        return Array.from(this._services.keys());
    }
}

// Extend Window interface for development debugging
declare global {
    interface Window {
        __smart_registry__: SmartRegistry;
        registry: SmartRegistry;
    }
}

interface ISmartRegistry {
    register<K extends keyof ServiceMap>(name: K, instance: ServiceMap[K]): void;
    register(name: ServiceName, instance: unknown): void;
    get<K extends keyof ServiceMap>(name: K): ServiceMap[K] | null;
    get<T>(name: ServiceName): T | null;
    whenReady<K extends keyof ServiceMap>(name: K): Promise<ServiceMap[K]>;
    whenReady<T>(name: ServiceName): Promise<T>;
    has(name: ServiceName): boolean;
}

// Internal singleton with safe window exposure
let instance: SmartRegistry;

if (typeof window !== 'undefined') {
    const win = window as unknown as Record<string, SmartRegistry | undefined>;
    if (!win.__smart_registry__) {
        win.__smart_registry__ = new SmartRegistry();
    }
    instance = win.__smart_registry__;

    // Phase 6.4: Expose to window ONLY in development for debugging
    const isDev = import.meta.env.DEV;
    if (isDev) {
        win.registry = instance;
    }
} else {
    // SSR or Test environment
    instance = new SmartRegistry();
}

export const registry = instance;
export default registry;
export type { ISmartRegistry };
